import { useState, useRef, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Checkbox, FormControlLabel,
  Button, Alert, CircularProgress, IconButton, Chip, MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate, useParams } from 'react-router-dom';
import { useUser } from '../../context/userContext';
import handleAgentName from '../../utils/userUtils';
import { createDesignRequest, getDesignRequest, updateDesignRequest } from '../../api/designRequestApi';
import { getAllAgentGroups, getAgentsByGroupId } from '../../api/userApi';

const PP = "'Poppins', sans-serif";

const sectionTitle = (text) => (
  <Typography sx={{
    fontSize: '0.72rem', fontWeight: 600, color: '#64748B',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    mt: 2.5, mb: 1.5, pb: 0.5, borderBottom: '1px solid #E2E8F0',
    fontFamily: PP,
  }}>
    {text}
  </Typography>
);

const fieldLabel = (text, required) => (
  <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#374151', mb: 0.5, fontFamily: PP }}>
    {text} {required && <span style={{ color: '#EF4444' }}>*</span>}
  </Typography>
);

const inputSx = { fontFamily: PP, fontSize: '0.85rem' };

const MAX_IMAGES = 10;

// Resize + compress images before encoding. 8 full-res rooftop photos as base64 were
// tens of MB and failing the submit; this caps the longest edge at 1600px and re-encodes
// as JPEG ~0.72, shrinking each photo ~10×. Non-images fall back to a plain read.
function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    if (!file.type || !file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      try {
        const MAX = 1600;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          const scale = MAX / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.72));
      } catch (e) { reject(e); }
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image load failed')); };
    img.src = url;
  });
}

const apiBase = process.env.REACT_APP_API_BASE_URL?.replace(/\/api$/, '') || '';

export default function RequestDesignForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const { user } = useUser();

  const agentDisplayName = handleAgentName(user?.firstName, user?.lastName, user?.username);

  // Group Admin / admins can raise the request on behalf of a chosen team + agent.
  const canChooseAgent = (user?.roles || []).some(r =>
    ['Group Admin', 'Super Admin', 'Admin', 'Astern Admin'].includes(r));
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState('');
  const [teamAgents, setTeamAgents] = useState([]);
  const [onBehalfAgentId, setOnBehalfAgentId] = useState('');

  useEffect(() => {
    if (!canChooseAgent || isEditMode) return;
    getAllAgentGroups()
      .then(r => setTeams(Array.isArray(r.data) ? r.data : []))
      .catch(() => setTeams([]));
  }, [canChooseAgent, isEditMode]);

  const handleTeamChange = (gid) => {
    setTeamId(gid);
    setOnBehalfAgentId('');
    if (gid) {
      getAgentsByGroupId(gid)
        .then(r => setTeamAgents(Array.isArray(r.data) ? r.data : []))
        .catch(() => setTeamAgents([]));
    } else {
      setTeamAgents([]);
    }
  };

  const [customerName, setCustomerName] = useState('');
  const [panels, setPanels] = useState('');
  const [address, setAddress] = useState('');
  const [mapLink, setMapLink] = useState('');
  const [remarks, setRemarks] = useState('');
  const [designGoogleMap, setDesignGoogleMap] = useState(false);
  const [designDroneShot, setDesignDroneShot] = useState(false);

  // Existing images (edit mode) — shown as read-only links
  const [existingImageUrls, setExistingImageUrls] = useState([]);
  // Newly selected images to append
  const [imageFiles, setImageFiles] = useState([]); // [{file, dataBase64, previewUrl, fileName}]
  const fileInputRef = useRef(null);

  const [loadingDetail, setLoadingDetail] = useState(isEditMode);
  const [loadError, setLoadError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  // Load existing data in edit mode
  useEffect(() => {
    if (!isEditMode) return;
    setLoadingDetail(true);
    setLoadError('');
    getDesignRequest(id)
      .then(res => {
        const d = res.data;
        setCustomerName(d.customerName || '');
        setPanels(d.panels != null ? String(d.panels) : '');
        setAddress(d.address || '');
        setMapLink(d.mapLink || '');
        setRemarks(d.remarks || '');
        setDesignGoogleMap(Boolean(d.designGoogleMap));
        setDesignDroneShot(Boolean(d.designDroneShot));
        setExistingImageUrls(d.imageUrls || []);
      })
      .catch(() => setLoadError('Failed to load design request.'))
      .finally(() => setLoadingDetail(false));
  }, [id, isEditMode]);

  const panelsNum = parseInt(panels, 10);
  const panelsValid = panels !== '' && !isNaN(panelsNum) && panelsNum > 0;
  const kwpAuto = panelsValid ? (panelsNum * 0.64).toFixed(2) : null;

  const validate = () => {
    const errs = {};
    if (!customerName.trim()) errs.customerName = 'Customer name is required';
    if (!panelsValid) errs.panels = 'Number of panels is required';
    if (!address.trim()) errs.address = 'Address is required';
    if (!mapLink.trim()) errs.mapLink = 'Map link is required';
    if (canChooseAgent && !isEditMode && !onBehalfAgentId) errs.agent = 'Please choose the team and agent';
    return errs;
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // Total cap = MAX_IMAGES; account for existing + already-queued
    const alreadyHave = existingImageUrls.length + imageFiles.length;
    const remaining = Math.max(0, MAX_IMAGES - alreadyHave);
    const toAdd = files.slice(0, remaining);

    const parsed = await Promise.all(
      toAdd.map(async (file) => {
        const dataBase64 = await readFileAsBase64(file);
        const previewUrl = URL.createObjectURL(file);
        // Encoded as JPEG above, so normalise the extension to .jpg.
        return { file, dataBase64, previewUrl, fileName: file.name.replace(/\.[^.]+$/, '') + '.jpg' };
      })
    );

    setImageFiles(prev => [...prev, ...parsed]);
    e.target.value = '';
  };

  const removeImage = (idx) => {
    setImageFiles(prev => {
      const next = [...prev];
      URL.revokeObjectURL(next[idx].previewUrl);
      next.splice(idx, 1);
      return next;
    });
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length) {
      setFieldErrors(errs);
      return;
    }
    setFieldErrors({});
    setError('');
    setSubmitting(true);

    try {
      const dto = {
        DesignGoogleMap: designGoogleMap,
        DesignDroneShot: designDroneShot,
        CustomerName: customerName.trim(),
        Panels: panelsValid ? panelsNum : null,
        Address: address.trim(),
        MapLink: mapLink.trim(),
        Remarks: remarks.trim() || null,
        ...(canChooseAgent && onBehalfAgentId ? { AgentId: Number(onBehalfAgentId) } : {}),
        Images: imageFiles.map(img => ({
          FileName: img.fileName,
          DataBase64: img.dataBase64,
        })),
      };

      if (isEditMode) {
        await updateDesignRequest(id, dto);
      } else {
        await createDesignRequest(dto);
      }
      navigate('/design-requests');
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || (isEditMode ? 'Failed to update design request.' : 'Failed to submit design request. Please try again.');
      setError(typeof msg === 'string' ? msg : (isEditMode ? 'Failed to update design request.' : 'Failed to submit design request.'));
    } finally {
      setSubmitting(false);
    }
  };

  const totalImageCount = existingImageUrls.length + imageFiles.length;

  if (loadingDetail) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', px: 2, py: 2 }}>
        <Alert severity="error" sx={{ fontFamily: PP }}>{loadError}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', px: 2, py: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton size="small" onClick={() => navigate('/design-requests')}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography sx={{ fontFamily: PP, fontWeight: 700, fontSize: '1.1rem', color: '#0C1B3A' }}>
          {isEditMode ? `Edit Design Request D${id}` : 'Request Design'}
        </Typography>
      </Box>

      <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2, p: 3 }}>

        {/* Agent — Group Admin/admins pick team + agent; everyone else is themselves */}
        {sectionTitle('Agent')}
        {canChooseAgent && !isEditMode ? (
          <Box>
            {fieldLabel('Team', true)}
            <TextField
              select fullWidth size="small"
              value={teamId}
              onChange={e => handleTeamChange(e.target.value)}
              inputProps={{ style: inputSx }}
              SelectProps={{ sx: inputSx }}
              sx={{ mb: 2 }}
            >
              <MenuItem value=""><em>Select a team</em></MenuItem>
              {teams.map(g => (
                <MenuItem key={g.groupId} value={g.groupId}>{g.groupName ?? `#${g.groupId}`}</MenuItem>
              ))}
            </TextField>

            {fieldLabel('Agent', true)}
            <TextField
              select fullWidth size="small"
              value={onBehalfAgentId}
              onChange={e => setOnBehalfAgentId(e.target.value)}
              disabled={!teamId}
              error={!!fieldErrors.agent}
              helperText={fieldErrors.agent || (!teamId ? 'Select a team first' : '')}
              inputProps={{ style: inputSx }}
              SelectProps={{ sx: inputSx }}
              sx={{ mb: 2 }}
            >
              <MenuItem value=""><em>Select an agent</em></MenuItem>
              {teamAgents.map(a => (
                <MenuItem key={a.userId} value={a.userId}>
                  {handleAgentName(a.firstName, a.lastName, a.alias)}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        ) : (
          <Box>
            {fieldLabel('Agent Name')}
            <TextField
              fullWidth
              size="small"
              value={agentDisplayName}
              disabled
              inputProps={{ style: inputSx }}
              sx={{ mb: 2 }}
            />
          </Box>
        )}

        {/* Customer */}
        {sectionTitle('Customer')}
        <Box>
          {fieldLabel('Customer Name', true)}
          <TextField
            fullWidth size="small"
            value={customerName}
            onChange={e => setCustomerName(e.target.value)}
            error={!!fieldErrors.customerName}
            helperText={fieldErrors.customerName}
            inputProps={{ style: inputSx }}
            sx={{ mb: 2 }}
          />

          {fieldLabel('Number of Panels', true)}
          <TextField
            fullWidth size="small"
            type="number"
            placeholder="e.g. 16"
            value={panels}
            onChange={e => setPanels(e.target.value)}
            error={!!fieldErrors.panels}
            helperText={fieldErrors.panels}
            inputProps={{ style: inputSx, min: 1 }}
            sx={{ mb: 0.5 }}
          />
          {panelsValid && (
            <Typography sx={{ fontFamily: PP, fontSize: '0.78rem', color: '#64748B', mb: 1.5 }}>
              Auto: {panelsNum} x 640Wp = {kwpAuto} kWp
            </Typography>
          )}
          {!panelsValid && <Box sx={{ mb: 1.5 }} />}

          {fieldLabel('Address', true)}
          <TextField
            fullWidth size="small" multiline rows={2}
            value={address}
            onChange={e => setAddress(e.target.value)}
            error={!!fieldErrors.address}
            helperText={fieldErrors.address}
            inputProps={{ style: inputSx }}
            sx={{ mb: 2 }}
          />

          {fieldLabel('Map Link', true)}
          <TextField
            fullWidth size="small"
            placeholder="Google Maps URL"
            value={mapLink}
            onChange={e => setMapLink(e.target.value)}
            error={!!fieldErrors.mapLink}
            helperText={fieldErrors.mapLink}
            inputProps={{ style: inputSx }}
            sx={{ mb: 2 }}
          />
        </Box>

        {/* Design Type */}
        {sectionTitle('Design Type')}
        <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={designGoogleMap}
                onChange={e => setDesignGoogleMap(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography sx={{ fontFamily: PP, fontSize: '0.85rem' }}>
                Google Map (SolarEdge only)
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={designDroneShot}
                onChange={e => setDesignDroneShot(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography sx={{ fontFamily: PP, fontSize: '0.85rem' }}>
                Drone Shot
              </Typography>
            }
          />
        </Box>

        {/* Remarks */}
        {sectionTitle('Remarks')}
        <Box>
          {fieldLabel('Remarks (optional)')}
          <TextField
            fullWidth size="small" multiline rows={3}
            value={remarks}
            onChange={e => setRemarks(e.target.value)}
            inputProps={{ style: inputSx }}
            sx={{ mb: 2 }}
          />
        </Box>

        {/* Images */}
        {sectionTitle('Images')}
        <Box sx={{ mb: 2 }}>
          {isEditMode && existingImageUrls.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontFamily: PP, fontSize: '0.78rem', color: '#64748B', mb: 0.75 }}>
                Existing images ({existingImageUrls.length}):
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {existingImageUrls.map((url, i) => {
                  const fullUrl = url.startsWith('http') ? url : `${apiBase}${url}`;
                  return (
                    <a
                      key={i}
                      href={fullUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontFamily: PP, fontSize: '0.8rem', color: '#1D4ED8', textDecoration: 'none' }}
                    >
                      Image {i + 1}
                    </a>
                  );
                })}
              </Box>
            </Box>
          )}

          <Typography sx={{ fontFamily: PP, fontSize: '0.78rem', color: '#64748B', mb: 1 }}>
            {isEditMode
              ? `Add more photos (up to ${MAX_IMAGES} total, currently ${existingImageUrls.length})`
              : `Upload up to ${MAX_IMAGES} photos (rooftop, site, etc.)`}
          </Typography>

          <Button
            variant="outlined"
            size="small"
            startIcon={<UploadFileIcon />}
            disabled={totalImageCount >= MAX_IMAGES}
            onClick={() => fileInputRef.current?.click()}
            sx={{ fontFamily: PP, fontSize: '0.8rem', textTransform: 'none', mb: 2 }}
          >
            Choose Photos
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />

          {imageFiles.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {imageFiles.map((img, idx) => (
                <Box
                  key={idx}
                  sx={{
                    position: 'relative', width: 90, height: 90,
                    border: '1px solid #E2E8F0', borderRadius: 1, overflow: 'hidden',
                  }}
                >
                  <img
                    src={img.previewUrl}
                    alt={img.fileName}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => removeImage(idx)}
                    sx={{
                      position: 'absolute', top: 2, right: 2,
                      bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', p: 0.3,
                      '&:hover': { bgcolor: 'rgba(200,0,0,0.75)' },
                    }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          {totalImageCount >= MAX_IMAGES && (
            <Chip
              label={`Maximum ${MAX_IMAGES} images reached`}
              size="small"
              color="warning"
              sx={{ fontFamily: PP, fontSize: '0.75rem', mt: 1 }}
            />
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, fontFamily: PP, fontSize: '0.83rem' }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate('/design-requests')}
            disabled={submitting}
            sx={{ fontFamily: PP, fontSize: '0.83rem', textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ fontFamily: PP, fontSize: '0.83rem', textTransform: 'none', bgcolor: '#0C1B3A', '&:hover': { bgcolor: '#1a2e5c' } }}
          >
            {submitting
              ? (isEditMode ? 'Saving...' : 'Submitting...')
              : (isEditMode ? 'Save Changes' : 'Submit Request')}
          </Button>
        </Box>

      </Paper>
    </Box>
  );
}
